import type { FrameMaskOf, OccluderRule } from "daedalus-core";
import type { ConeSight, PropView } from "./prop-view.ts";

/**
 * The occluder fade over a character (ADR-040, ADR-071 decision 10): one function the game calls with its
 * local character and the editor with its stand-in, so a prop fades in the editor exactly where it fades in
 * the game. The body box, the rule and which props are handed to it are the caller's; presence props are
 * never handed to it on either page.
 */

/**
 * A body box the caller keeps and the fade refills on every call, so a fade run each frame allocates nothing.
 * World pixels, every edge inclusive.
 */
export interface BodyBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/**
 * A character's body size in world pixels: `width` centred on the feet, `height` standing on the feet line.
 * A registered `StandInDefinition` is one.
 */
export interface BodySize {
  readonly width: number;
  readonly height: number;
}

/**
 * Fades the props of every view that hide most of a character standing at a feet point, and restores the
 * rest. The body box is placed as the editor's `standInBody` places it: `size.width` pixels from
 * `x - floor(width / 2)`, `size.height` pixels standing on the feet line. With no rule nothing fades and every
 * prop is restored, which is how a game that registers none fades nothing. With a cone of sight, the same walk
 * eases the props in it toward its alpha and the rest back (`PropView.fadeOccluders`, ADR-061's amendment for
 * P1.9-T75); the game passes one for its local character, the editor's stand-in none.
 *
 * @param views The prop views to fade in.
 * @param body The box to refill with the character's body.
 * @param size The body's size.
 * @param x Feet x in world pixels, whole.
 * @param y Feet line in world pixels, whole.
 * @param depth The character's depth.
 * @param rule The occluder rule, or none.
 * @param maskOf The solid pixels of a frame, by frame id.
 * @param exempt Whether a view's prop, by id, is never faded; absent, none is.
 * @param sight The cone of sight to fade props in as well; absent, none.
 * @returns How many props are faded over the body now.
 * @throws RangeError when the feet are off whole pixels and a prop reaches the pixel count.
 */
export function fadeOverCharacter(views: Iterable<PropView>, body: BodyBox, size: BodySize, x: number, y: number, depth: number, rule: OccluderRule | undefined, maskOf: FrameMaskOf, exempt?: (view: PropView, id: string) => boolean, sight?: ConeSight): number {
  if (rule === undefined) {
    for (const view of views) {
      view.restoreAlpha();
    }
    return 0;
  }
  body.x0 = x - Math.floor(size.width / 2);
  body.y0 = y - size.height;
  body.x1 = body.x0 + size.width - 1;
  body.y1 = y - 1;
  let faded = 0;
  for (const view of views) {
    faded += view.fadeOccluders(body, depth, rule, maskOf, exempt === undefined ? undefined : (id) => exempt(view, id), sight);
  }
  return faded;
}
